// Lib
import { forEach, isFunction } from 'lodash';

// Utils
import globalLogger from '../logger/logger';
import { runIfAvailable } from './analyticsInvocationUtils';
import { getUtmData } from './utmClientUtils';
import { waitForValue } from '../../common/utils/functionUtils';
import platformSingleton from '../platform/platformSingleton';
import { isPlatformDesktopPwa, isPlatformElectronMac, isPlatformIframe } from '../platform/utils/platformDetailsUtils';

// Constants
import { AMPLITUDE_USER_PROPS, TRACKED_FEATURES } from '../../common/analytics/statsConstants';
import { EVENT_TYPE_NAMES } from '../../common/analytics/amplitudeEventTypesUtil';
import { AMPLITUDE_SESSION_TIMEOUT, AMPLITUDE_START_SESSION_EVENT_TYPE } from './amplitudeSessionConstants';
import { initialiseElectronAmplitudeLifecycleTracking } from './electronAmplitudeLifecycleService';
import type { ClientConfig } from '../../common/config/configTypes';

type AmplitudeProperties = object;
type AmplitudePropertyMap = Record<string, unknown>;

type AmplitudeIdentify = {
    append: (property: string, value: unknown) => AmplitudeIdentify;
    set: (property: string, value: unknown) => AmplitudeIdentify;
    setOnce: (property: string, value: unknown) => AmplitudeIdentify;
};

type AmplitudeInstance = {
    enableTracking: () => void;
    identify: (identify: AmplitudeIdentify) => void;
    init: (apiKey: string, userId: null, options: AmplitudeOptions, callback: () => void) => void;
    logEvent: (eventType: string, eventProperties?: AmplitudeProperties) => void;
    options?: { deviceId?: string };
    setOptOut: (optOut: boolean) => void;
    resetSessionId: () => void;
    setUserId: (userId: string | null) => void;
    setUserProperties: (properties: AmplitudeProperties) => void;
    setVersionName: (versionName: string) => void;
};

type AmplitudeOptions = {
    deviceIdFromUrlParam: boolean;
    forceHttps: boolean;
    includeGclid: boolean;
    includeReferrer: boolean;
    includeUtm: boolean;
    onNewSessionStart: () => void;
    sessionTimeout: number;
    trackingOptions: { ip_address: boolean };
    transport: 'beacon';
};

type AmplitudeSdk = {
    Identify: new () => AmplitudeIdentify;
    getInstance: () => AmplitudeInstance;
    identify: (identify: AmplitudeIdentify) => void;
};

type AmplitudeEvent = {
    eventProperties?: AmplitudeProperties;
    eventType?: string;
    userProperties?: AmplitudeProperties;
};

type AmplitudeInitialisationConfig = {
    analytics?: Pick<ClientConfig['analytics'], 'amplitudeApiKey'>;
};

declare global {
    interface Window {
        amplitude: AmplitudeSdk;
    }
}

let hasInitialisedAmplitude = false;

const isAmplitudeAvailable = () => !!window.amplitude && isFunction(window.amplitude.getInstance);
const isAmplitudeTrackingAvailable = () => hasInitialisedAmplitude && isAmplitudeAvailable();
const runIfAmplitudeIsAvailable = runIfAvailable('Amplitude', isAmplitudeAvailable);
const runIfAmplitudeTrackingIsAvailable = runIfAvailable('Amplitude', isAmplitudeTrackingAvailable);

const logger = globalLogger.createChannel('amplitude-service');

/** Gets the Amplitude device identifier when the browser SDK is available. */
export const getAmplitudeDeviceId = runIfAmplitudeTrackingIsAvailable(function getAmplitudeDeviceId() {
    return window.amplitude.getInstance()?.options?.deviceId;
});

// Prevent a possible race condition where the device ID isn't yet available when requested
// This race condition is more likely for a guest user visiting a shared board on a cold-start
export const waitForAmplitudeDeviceId = async (): Promise<string | null> => {
    const amplitude = window.amplitude;

    if (!hasInitialisedAmplitude || !amplitude || !isFunction(window.amplitude.getInstance)) return null;

    return (await waitForValue(() => amplitude.getInstance()?.options?.deviceId)) ?? null;
};

export const enableAmplitudeTracking = runIfAmplitudeIsAvailable(function enableAmplitudeTracking(enable: boolean) {
    const amplitudeInstance = window.amplitude.getInstance();

    if (!hasInitialisedAmplitude) return Promise.resolve();

    try {
        if (!enable) {
            amplitudeInstance.setOptOut(true);
            return Promise.resolve();
        }

        amplitudeInstance.setOptOut(false);
        amplitudeInstance.enableTracking();

        // Sensitive data audit: SAFE — Static success message with no user data
        logger.info('%cAmplitude analytics successfully enabled', 'color: green');
    } catch (error: unknown) {
        // Sensitive data audit: SAFE — warn auto-sanitized (static message, error object)
        logger.warn('Failed to enable Amplitude tracking', error);
    }

    return Promise.resolve();
});

/** Associates browser analytics with the current user. */
export const setAmplitudeUser = runIfAmplitudeTrackingIsAvailable(function setAmplitudeUser(user: { _id: string }) {
    window.amplitude.getInstance().setUserId(user._id);
});

/** Removes the current browser analytics user association. */
export const clearAmplitudeUser = runIfAmplitudeTrackingIsAvailable(function clearAmplitudeUser() {
    window.amplitude.getInstance().setUserId(null);
});

/** Sends browser analytics user properties, including the current application platform. */
export const sendAmplitudeUserProperties = runIfAmplitudeTrackingIsAvailable(function sendAmplitudeUserProperties({
    userProperties = {},
}: Pick<AmplitudeEvent, 'userProperties'>) {
    const amplitudeUserProperties: AmplitudePropertyMap = { ...userProperties };
    const hasExampleBoardId = !!amplitudeUserProperties[AMPLITUDE_USER_PROPS.EXAMPLE_BOARDS];
    if (hasExampleBoardId) {
        const identify = new window.amplitude.Identify().append(
            AMPLITUDE_USER_PROPS.EXAMPLE_BOARDS,
            amplitudeUserProperties[AMPLITUDE_USER_PROPS.EXAMPLE_BOARDS],
        );
        window.amplitude.identify(identify);
    }

    delete amplitudeUserProperties[AMPLITUDE_USER_PROPS.EXAMPLE_BOARDS];

    amplitudeUserProperties[AMPLITUDE_USER_PROPS.APPLICATION_PLATFORM] = platformSingleton.applicationPlatform;

    window.amplitude.getInstance().setUserProperties(amplitudeUserProperties);
});

/** Sends an event to Amplitude without forwarding it to Engagement. */
export const sendAmplitudeEventWithoutForwarding = runIfAmplitudeTrackingIsAvailable(function sendAmplitudeEvent({
    eventType,
    eventProperties,
    userProperties,
}: AmplitudeEvent) {
    sendAmplitudeUserProperties({ userProperties });

    eventType && window.amplitude.getInstance().logEvent(eventType, eventProperties);
});

/** Sends an event to both Amplitude and Amplitude Engagement. */
export const sendAmplitudeEvent = runIfAmplitudeTrackingIsAvailable(function sendAmplitudeEvent({
    eventType,
    eventProperties,
    userProperties,
}: AmplitudeEvent) {
    sendAmplitudeEventWithoutForwarding({ eventType, eventProperties, userProperties });
    if (eventType) window.engagement?.forwardEvent({ event_type: eventType, event_properties: eventProperties });
});

/** Records attribution parameters that the browser SDK does not retain itself. */
const manuallySetUtmData = runIfAmplitudeTrackingIsAvailable(function manuallySetAmplitudeUtmData() {
    const utmData = getUtmData(document.location.href, document.referrer);
    if (!utmData) return;

    const identify = new window.amplitude.Identify();

    forEach(utmData, (value, key) => {
        identify.set(key, value);
        identify.setOnce(`initial_${key}`, value);
    });

    window.amplitude.identify(identify);
});

export const initialiseAmplitude = runIfAmplitudeIsAvailable(function initialiseAmplitude(
    config: AmplitudeInitialisationConfig,
) {
    if (hasInitialisedAmplitude) return;

    const amplitudeApiKey = config.analytics?.amplitudeApiKey;
    if (!amplitudeApiKey) return;

    const amplitudeConfig: AmplitudeOptions = {
        forceHttps: true,

        // Allows an amplitude device ID to be specified via the "amp_device_id" parameter if set
        deviceIdFromUrlParam: true,

        // Google click identifier (if used)
        includeGclid: true,

        // UTM (Urchin traffic monitor) parameters
        includeUtm: true,

        // HTTP referrer header
        includeReferrer: true,

        // This is to ensure that requests will be made regardless of exiting page
        transport: 'beacon',

        sessionTimeout: AMPLITUDE_SESSION_TIMEOUT,
        trackingOptions: {
            ip_address: false,
        },
        onNewSessionStart: () => {
            window.amplitude.getInstance().logEvent(AMPLITUDE_START_SESSION_EVENT_TYPE);
        },

        // FIXME It looks like this is causing the loss first viewed marketing page
        // This is required to support UTM parameters set on the URL via public boards
        // saveParamsReferrerOncePerSession: false,
    };

    const amplitudeInstance = window.amplitude.getInstance();
    try {
        amplitudeInstance.init(amplitudeApiKey, null, amplitudeConfig, () =>
            // Sensitive data audit: SAFE — Static success message with no user data
            logger.info('%cAmplitude analytics successfully loaded', 'color: green'),
        );
    } catch (error: unknown) {
        // Sensitive data audit: SAFE — warn auto-sanitized (static message, error object)
        logger.warn('Failed to initialise Amplitude', error);
        return;
    }

    hasInitialisedAmplitude = true;

    if (isPlatformElectronMac(platformSingleton)) initialiseElectronAmplitudeLifecycleTracking();

    // Set application platform on initialisation, since often the first events come from the server and won't have platform info
    const identify = new window.amplitude.Identify();
    identify.set(AMPLITUDE_USER_PROPS.APPLICATION_PLATFORM, platformSingleton.applicationPlatform);
    amplitudeInstance.identify(identify);
});

/** Adds platform-specific browser analytics data after initialisation. */
export const initialiseAmplitudeData = runIfAmplitudeTrackingIsAvailable(function initialiseAmplitudeData(
    config: Partial<Pick<ClientConfig, 'appVersion'>>,
) {
    if (isPlatformIframe(platformSingleton)) {
        sendAmplitudeEvent({
            eventType: EVENT_TYPE_NAMES.LOADED_IN_IFRAME,
            eventProperties: {
                referrer: document.referrer,
            },
        });
    }

    if (isPlatformElectronMac(platformSingleton)) {
        if (config.appVersion) window.amplitude.getInstance().setVersionName(config.appVersion);
        sendAmplitudeUserProperties({
            userProperties: {
                [AMPLITUDE_USER_PROPS.FEATURE]: { [TRACKED_FEATURES.DESKTOP_APP]: true },
            },
        });
    } else if (isPlatformDesktopPwa(platformSingleton)) {
        sendAmplitudeUserProperties({
            userProperties: {
                [AMPLITUDE_USER_PROPS.FEATURE]: { [TRACKED_FEATURES.PWA]: true },
            },
        });
    } else {
        // Try to classify users as
        manuallySetUtmData();
    }
});
